import {
	ArrowRight,
	ChevronLeft,
	ChevronRight,
	Image,
	PlayCircle,
	X,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import {
	ACTIVE_NOTICES_PARAMS,
	useMarkNoticeAsRead,
	useNotices,
} from '@/api/queries/notices-queries'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/lib/utils'
import { getNoticeMediaKind } from '@/utils/notice-media'
import { NOTICE_TONE } from './notice-tone'

/**
 * Faixa fixa abaixo do header com os avisos de prioridade média não lidos.
 * Com mais de um aviso, alterna automaticamente (pausa no hover/foco) e permite navegar.
 * Fechar o aviso o marca como lido.
 */
export function NoticeBanner() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { data } = useNotices(ACTIVE_NOTICES_PARAMS)
	const { mutate: markNoticeAsRead } = useMarkNoticeAsRead()

	// Esconde na hora ao fechar, sem esperar o refetch da lista
	const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())
	const [index, setIndex] = useState(0)

	const notices = useMemo(
		() =>
			(data?.items ?? []).filter(
				(notice) =>
					notice.priority === 'medium' &&
					!notice.isRead &&
					!dismissedIds.has(notice.id),
			),
		[data?.items, dismissedIds],
	)

	if (notices.length === 0) return null

	const total = notices.length
	const current = notices[index % total]
	const position = (index % total) + 1
	const tone = NOTICE_TONE[current.type]
	const Icon = tone.icon

	const goTo = (step: number) =>
		setIndex((value) => (value + step + total) % total)

	const handleDismiss = () => {
		const { id } = current
		setDismissedIds((ids) => new Set(ids).add(id))
		markNoticeAsRead(id, {
			onError: () =>
				setDismissedIds((ids) => {
					const next = new Set(ids)
					next.delete(id)
					return next
				}),
		})
	}

	const handleOpen = () =>
		navigate(ROUTES.DASHBOARD.NOTICE_DETAIL.replace(':id', current.id))

	return (
		<section
			aria-label={t('notices.banner.label')}
			className="group relative z-10 border-b bg-background shadow-sm motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2"
		>
			<Alert
				className={cn(
					'flex items-center gap-3 rounded-none border-0 border-l-4 px-4 py-2.5 md:gap-4 md:px-6',
					tone.surface,
					tone.accent,
				)}
			>
				{/* Ícone com anel pulsante */}
				<span className="relative flex size-9 shrink-0 items-center justify-center">
					<span
						className={cn(
							'absolute inset-0 rounded-full motion-safe:animate-ping [animation-duration:2.4s]',
							tone.pulse,
						)}
					/>
					<span
						className={cn(
							'relative flex size-9 items-center justify-center rounded-full shadow-sm',
							tone.iconWrap,
						)}
					>
						<Icon className="size-[18px]" />
					</span>
				</span>

				<button
					key={current.id}
					type="button"
					onClick={handleOpen}
					className="min-w-0 flex-1 cursor-pointer text-left motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1"
				>
					<div className="flex items-center gap-2">
						<span
							className={cn(
								'font-bold text-[10px] uppercase tracking-[0.14em]',
								tone.text,
							)}
						>
							{t(`administration.notificationsForm.type_${current.type}`)}
						</span>
						{getNoticeMediaKind(current.media) === 'video' && (
							<PlayCircle className="size-3.5 text-kaefer-medium-grey" />
						)}
						{getNoticeMediaKind(current.media) === 'image' && (
							<Image className="size-3.5 text-kaefer-medium-grey" />
						)}
					</div>
					<AlertTitle className="mb-0 truncate font-semibold text-kaefer-dark-grey text-sm leading-snug">
						{current.title}
					</AlertTitle>
					<AlertDescription className="line-clamp-1 text-kaefer-medium-grey text-xs">
						{current.message}
					</AlertDescription>
				</button>

				{total > 1 && (
					<div className="flex shrink-0 items-center rounded-full border border-kaefer-light-silver bg-background/80 shadow-xs">
						<Button
							type="button"
							variant="ghost"
							size="icon-sm"
							className="size-7 rounded-full hover:bg-kaefer-light-silver/60"
							aria-label={t('notices.banner.previous')}
							onClick={() => goTo(-1)}
						>
							<ChevronLeft className="size-4" />
						</Button>
						<span className="min-w-8 text-center font-semibold text-[11px] text-kaefer-dark-grey tabular-nums">
							{position}/{total}
						</span>
						<Button
							type="button"
							variant="ghost"
							size="icon-sm"
							className="size-7 rounded-full hover:bg-kaefer-light-silver/60"
							aria-label={t('notices.banner.next')}
							onClick={() => goTo(1)}
						>
							<ChevronRight className="size-4" />
						</Button>
					</div>
				)}

				<Button
					type="button"
					size="sm"
					variant="outline"
					onClick={handleOpen}
					className="hidden shrink-0 border-kaefer-dark-grey/20 bg-background/80 text-kaefer-dark-grey hover:bg-kaefer-dark-grey hover:text-white sm:inline-flex"
				>
					{t('notices.banner.details')}
					<ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
				</Button>

				<Button
					type="button"
					size="icon-sm"
					variant="ghost"
					onClick={handleDismiss}
					aria-label={t('notices.banner.dismiss')}
					title={t('notices.banner.dismiss')}
					className="shrink-0 rounded-full text-kaefer-medium-grey hover:bg-kaefer-dark-grey/10 hover:text-kaefer-dark-grey"
				>
					<X className="size-4" />
				</Button>
			</Alert>

			{/* Barra de tempo da rotação automática; o fim da animação avança para o próximo aviso */}
			{total > 1 && (
				<span
					key={`${current.id}-${index}`}
					aria-hidden="true"
					onAnimationEnd={() => goTo(1)}
					className={cn(
						'absolute bottom-0 left-0 h-0.5 w-full origin-left opacity-70 motion-reduce:hidden motion-safe:animate-[notice-progress_8s_linear_forwards] group-focus-within:[animation-play-state:paused] group-hover:[animation-play-state:paused]',
						tone.solid,
					)}
				/>
			)}
		</section>
	)
}
